<template>
  <div class="st-pagination">
    <transition name="fade" @after-leave="applyPendingView">
      <div v-show="showPagination">
        <div
          v-if="paginatedData.length"
          ref="pagesLabel"
          class="st-pagination__label"
          tabindex="-1"
        >
          <span v-if="label" class="st-pagination__label__title">
            {{ label }},
          </span>
          Sida {{ currentPage }} av {{ totalPages }}
        </div>
        <slot name="action-bar" />
        <slot :paginated-data="paginatedData" />
        <nav
          aria-label="Paginering"
          class="st-pagination__pagination"
          v-if="totalPages > 1"
        >
          <TransitionGroup
            name="pagination-transition"
            tag="ul"
            class="st-pagination__pagination__controls"
          >
            <li :key="'chevron-left'">
              <button
                class="st-pagination__pagination__controls__btn"
                :class="{
                  'st-pagination__pagination__controls__btn--disabled':
                    currentPage === 1,
                }"
                :disabled="currentPage === 1"
                @click="previousPage"
                aria-label="Bläddra till föregående sida"
                type="button"
              >
                <StIcon name="chevron" />
              </button>
            </li>
            <li v-for="(page, i) in paginationRange" :key="page">
              <span
                v-if="page === '...'"
                class="st-pagination__pagination__controls__truncate"
              >
                {{ page }}
              </span>
              <button
                v-else
                class="st-pagination__pagination__controls__btn"
                @click="changePage(page)"
                :aria-label="`Sida ${page}. ${
                  i === 0
                    ? '(Första sidan)'
                    : i === paginationRange.length - 1
                    ? '(Sista sidan)'
                    : ''
                }`"
                :aria-current="currentPage === page"
                type="button"
              >
                <span
                  class="st-pagination__pagination__controls__btn__text"
                  :class="{
                    'st-pagination__pagination__controls__btn__text--selected':
                      currentPage === page,
                  }"
                >
                  {{ page }}
                </span>
              </button>
            </li>
            <li :key="'chevron-right'">
              <button
                class="st-pagination__pagination__controls__btn"
                :class="{
                  'st-pagination__pagination__controls__btn--disabled':
                    currentPage === totalPages,
                }"
                :disabled="currentPage === totalPages"
                @click="nextPage"
                aria-label="Bläddra till nästa sida"
                type="button"
              >
                <StIcon name="chevron" />
              </button>
            </li>
          </TransitionGroup>
        </nav>
      </div>
    </transition>
    <transition name="fade" @after-leave="applyPendingView">
      <div v-if="showSpinner" class="st-pagination__loader">
        <StSpinner :loading-message="loadingMessage" />
      </div>
    </transition>
    <div
      ref="loadingFocusHolder"
      class="st-pagination__focus-holder"
      tabindex="-1"
      aria-live="assertive"
    >
      {{ loadingMessage }}
    </div>
  </div>
</template>
<script setup>
import { ref, computed, watch } from "vue";
import StIcon from "../icon/Icon.vue";
import StSpinner from "../spinner/Spinner.vue";

const emit = defineEmits(["change-page"]);

const props = defineProps({
  label: String,
  dataToPaginate: Array,
  rowsPerPage: {
    type: Number,
    default: 10,
  },
  isLoading: Boolean,
  totalNumberOfPages: [Number, String],
  overrideCurrentPageTo: Number,
  loadingMessage: {
    type: String,
    default: "Laddar...",
  },
  setAutoFocusOnLabel: {
    type: Boolean,
    default: true,
  },
});

const localCurrentPage = ref(1);
const pagesLabel = ref(null);
const loadingFocusHolder = ref(null);
const showPagination = ref(!props.isLoading);
const showSpinner = ref(props.isLoading);
const pendingShowSpinner = ref(props.isLoading);

const totalPages = computed(() => {
  if (props.totalNumberOfPages) {
    return props.totalNumberOfPages;
  } else {
    return Math.ceil(props.dataToPaginate.length / props.rowsPerPage);
  }
});

const startViewTransition = (loading) => {
  pendingShowSpinner.value = loading;
  const leavingView = loading ? showPagination : showSpinner;

  if (leavingView.value) {
    leavingView.value = false;
    return;
  }
  applyPendingView();
};

watch(
  () => props.isLoading,
  (loading) => {
    startViewTransition(loading);

    if (!loading && props.setAutoFocusOnLabel) {
      setTimeout(() => {
        if (pagesLabel.value) pagesLabel.value.focus();
      }, 100);
    } else {
      if (loadingFocusHolder.value) loadingFocusHolder.value.focus();
    }
  },
);

const applyPendingView = () => {
  showSpinner.value = pendingShowSpinner.value;
  showPagination.value = !pendingShowSpinner.value;
};

const currentPage = computed(() =>
  props.overrideCurrentPageTo
    ? props.overrideCurrentPageTo
    : localCurrentPage.value,
);

const paginatedData = computed(() => {
  if (props.overrideCurrentPageTo) {
    return props.dataToPaginate.slice(0, props.rowsPerPage);
  } else {
    const start = (localCurrentPage.value - 1) * props.rowsPerPage;
    const end = start + props.rowsPerPage;
    return props.dataToPaginate.slice(start, end);
  }
});

const paginationRange = computed(() => {
  const pages = [];
  const totalPagesValue = totalPages.value;
  const currentPageValue = currentPage.value;

  if (totalPagesValue <= 7) {
    renderAllButtons(totalPagesValue, pages);
  } else {
    pages.push(1);

    if (currentPageValue > 4) {
      pages.push("...");
    } else if (currentPageValue > 3) {
      pages.push(2);
    }

    const startPage = Math.max(2, currentPageValue - 1);
    const endPage = Math.min(totalPagesValue - 1, currentPageValue + 1);

    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPagesValue) {
        pages.push(i);
      }
    }

    if (currentPageValue < totalPagesValue - 3) {
      pages.push("...");
    } else if (currentPageValue < totalPagesValue - 2) {
      pages.push(totalPagesValue - 1);
    }

    pages.push(totalPagesValue);
  }

  return pages;
});

const renderAllButtons = (totalPages, pages) => {
  for (let i = 1; i <= totalPages; i++) {
    pages.push(i);
  }
};

const changePage = (page) => {
  if (page !== currentPage.value) {
    localCurrentPage.value = page;
    handlePageChange(page);
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    localCurrentPage.value++;
    handlePageChange(currentPage.value + 1);
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    localCurrentPage.value--;
    handlePageChange(currentPage.value - 1);
  }
};

const handlePageChange = (page) => {
  emit("change-page", page);
  if (pagesLabel.value) pagesLabel.value.focus();
};
</script>
<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-pagination {
  &__label {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    color: var(--text-color-primary);
    margin-bottom: rem-calc(16);
    font-family: var(--font-primary);
    font-weight: var(--font-weight-regular);
    font-size: rem-calc(16);
    line-height: rem-calc(21);

    &:focus {
      outline: none;
    }

    @include large {
      flex-direction: row;
      & > :first-child {
        margin-right: rem-calc(8);
      }
    }

    &__title {
      font-family: var(--font-secondary);
      font-size: rem-calc(18);
      font-weight: var(--font-weight-bold);
      line-height: rem-calc(27);
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: rem-calc(32);

    &__controls {
      display: flex;
      justify-content: center;
      align-items: center;
      list-style: none;
      padding: 0;
      margin: 0;

      &__truncate {
        padding: rem-calc(8);
        font-family: var(--font-primary);
        color: var(--text-color-primary);
        transition: 0.3s ease opacity, 0.3s ease transform;
      }

      &__btn {
        display: flex;
        height: rem-calc(32);
        width: rem-calc(32);
        background: none;
        border: var(--border-width) solid transparent;
        font-family: var(--font-primary);
        font-size: rem-calc(16);
        justify-content: center;
        align-items: center;
        cursor: pointer;
        color: var(--text-color-primary);
        transition: 0.3s ease opacity, 0.3s ease transform;
        opacity: 1;
        transform: scale(1);

        &__text {
          color: var(--text-color-primary);
          padding: rem-calc(2) rem-calc(8);
          border-bottom: var(--border-width) solid transparent;
          line-height: rem-calc(21);

          &--selected {
            font-weight: var(--font-weight-bold);
            border-bottom: var(--border-width) solid var(--border-color-primary);
          }
        }

        &:focus {
          outline: none;
          border: var(--border-width) solid var(--border-color-quarternary);
        }

        &:hover {
          border: var(--border-width--thin) solid var(--border-color-primary);
        }

        &--disabled {
          cursor: not-allowed;
          &:hover {
            border-color: transparent;
          }
        }
      }

      & > :not(:last-child) {
        margin-right: rem-calc(4);
      }

      & > :first-child {
        .st-icon {
          transform: rotate(90deg);
        }
      }

      & > :last-child {
        .st-icon {
          transform: rotate(-90deg);
        }
      }
    }
  }

  &__loader {
    margin-top: rem-calc(24);
    text-align: center;
    @include large {
      margin-top: rem-calc(80);
    }
  }

  &__focus-holder {
    @include sr-only();
  }
}

.pagination-transition-enter-active,
.pagination-transition-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.pagination-transition-enter-from,
.pagination-transition-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
